import React, { useMemo, useState, useRef } from "react";
import { formatTranslationText } from "../../utils/textFormatter";
import { synthesizeSpeech } from "../../services/api";
import { pcmToWav } from "../../utils/audioConverter";

interface TranslationDisplayProps {
  text: string;
  isLoading: boolean;
  onRetranslate: () => void;
  hasTranscription: boolean;
  targetLang?: string;
}

const TranslationDisplay: React.FC<TranslationDisplayProps> = ({
  text,
  isLoading,
  onRetranslate,
  hasTranscription,
  targetLang = "en",
}) => {
  const [isTTSLoading, setIsTTSLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Split text into sentences for better readability
  const sentences = useMemo(() => {
    if (!text || !text.trim()) {
      return [];
    }
    return formatTranslationText(text);
  }, [text]);

  // Determine language type for TTS based on target language
  const getLanguageType = (lang: string): string => {
    const langMap: { [key: string]: string } = {
      zh: "Chinese",
      en: "English",
      ja: "Japanese",
      ko: "Korean",
      es: "Spanish",
      fr: "French",
      de: "German",
      it: "Italian",
      pt: "Portuguese",
      ru: "Russian",
      ar: "Arabic",
    };
    return langMap[lang.toLowerCase()] || "English";
  };

  // Handle TTS synthesis and playback
  const handlePlayTTS = async () => {
    if (!text || !text.trim() || isTTSLoading) {
      return;
    }

    setIsTTSLoading(true);
    try {
      const audioBlob = await synthesizeSpeech(
        text,
        "Cherry",
        getLanguageType(targetLang),
        false
      );

      // Convert PCM blob to WAV format for browser playback
      // qwen3-tts-flash returns PCM audio at 24kHz sample rate
      const arrayBuffer = await audioBlob.arrayBuffer();
      const pcmData = new Uint8Array(arrayBuffer);
      const wavBlob = pcmToWav(pcmData, 24000, 1, 16);

      // Create audio URL from WAV blob
      const audioUrl = URL.createObjectURL(wavBlob);

      // Create audio element and play
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      // Clean up URL when audio ends
      audio.addEventListener("ended", () => {
        URL.revokeObjectURL(audioUrl);
        setIsTTSLoading(false);
      });

      // Handle errors
      audio.addEventListener("error", (e) => {
        console.error("Audio playback error:", e);
        URL.revokeObjectURL(audioUrl);
        setIsTTSLoading(false);
      });

      await audio.play();
    } catch (error) {
      console.error("TTS synthesis error:", error);
      setIsTTSLoading(false);
    }
  };

  // Stop audio playback
  const handleStopTTS = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsTTSLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow-md flex flex-col min-h-[200px]">
      <div className="flex justify-between items-center mb-3 pb-2 border-b-2 border-gray-100">
        <h3 className="text-xl font-semibold text-gray-800 m-0">Translation</h3>
        <div className="flex gap-2">
          {text && text.trim() && (
            <button
              onClick={isTTSLoading ? handleStopTTS : handlePlayTTS}
              disabled={isLoading}
              className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-semibold transition-colors hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isTTSLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Playing...</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    />
                  </svg>
                  <span>Play Audio</span>
                </>
              )}
            </button>
          )}
          {hasTranscription && (
            <button
              onClick={onRetranslate}
              disabled={isLoading}
              className="px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-semibold transition-colors hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? "Translating..." : "Retranslate"}
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin"></div>
            <p className="text-gray-600 m-0">Translating...</p>
          </div>
        ) : sentences.length > 0 ? (
          <div className="flex flex-col gap-2">
            {sentences.map((sentence, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-1 border-l-4 border-primary-500 shadow-sm hover:shadow-md transition-shadow"
              >
                <p className="text-gray-800 text-base leading-relaxed m-0 break-words">
                  {sentence}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 italic text-center mt-8">
            Translated text will appear here...
          </p>
        )}
      </div>
    </div>
  );
};

export default TranslationDisplay;
